import PropTypes from "prop-types";
import { Box, Breadcrumbs, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { buildResultUrl } from "./search-dialog-constants";

const TYPE_LIST_URL = {
  Document: "/library/documents/all",
  Video: "/library/videos/all",
  Image: "/library/images/all",
  Audio: "/library/audios/all",
  News: "/news/all",
  HelpTopic: "/help",
  Contact: "/contacts",
};

const parseAssetPaths = (urlArray) =>
  (urlArray ?? [])
    .map((jsonStr) => {
      try {
        const crumbs = JSON.parse(jsonStr);
        const rootId = crumbs[0]?.key?.split("/")[1];
        return crumbs.map((crumb) => ({
          label: crumb.value,
          url: `/assets/${rootId}/${crumb.key}`,
        }));
      } catch {
        return [];
      }
    })
    .filter((p) => p.length > 0);

const crumbSx = {
  cursor: "pointer",
  color: "text.secondary",
  "&:hover": { textDecoration: "underline" },
};

const breadcrumbsSx = {
  "& .MuiBreadcrumbs-separator": { mx: 0.25 },
};

export const ResultBreadcrumb = ({ item, onResultClick }) => {
  const { t } = useTranslation();

  if (item.type === "Asset") {
    const paths = parseAssetPaths(item.url);
    if (!paths.length) return null;
    return (
      <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.25 }}>
        {paths.map((crumbs, pathIdx) => (
          <Breadcrumbs key={pathIdx} separator="›" sx={breadcrumbsSx}>
            {crumbs.map((crumb, i) => (
              <Typography
                key={crumb.url}
                variant="caption"
                onClick={(e) => {
                  e.stopPropagation();
                  onResultClick(crumb.url);
                }}
                sx={{
                  ...crumbSx,
                  fontWeight: i === crumbs.length - 1 ? 600 : 400,
                }}
              >
                {crumb.label}
              </Typography>
            ))}
          </Breadcrumbs>
        ))}
      </Box>
    );
  }

  const typeLabel = t(`search.types.${item.type}`, { defaultValue: item.type });
  const listUrl = TYPE_LIST_URL[item.type];
  const itemUrl = buildResultUrl(item);

  return (
    <Breadcrumbs separator="›" sx={{ mt: 0.5, ...breadcrumbsSx }}>
      <Typography
        variant="caption"
        onClick={
          listUrl
            ? (e) => {
                e.stopPropagation();
                onResultClick(listUrl);
              }
            : undefined
        }
        sx={listUrl ? crumbSx : { color: "text.secondary" }}
      >
        {typeLabel}
      </Typography>
      <Typography
        variant="caption"
        onClick={(e) => {
          e.stopPropagation();
          onResultClick(itemUrl);
        }}
        sx={crumbSx}
      >
        {item.title}
      </Typography>
    </Breadcrumbs>
  );
};

ResultBreadcrumb.propTypes = {
  item: PropTypes.object.isRequired,
  onResultClick: PropTypes.func,
};